/**
 * @component ry-color-picker
 * @category  form
 * @summary   Full-featured color picker (ry-ui-only): HSV saturation/value grid, hue slider, optional alpha slider, hex input, RGB/HSL channel inputs, format toggle. Defaults to a popover with a swatch trigger; add `[inline]` to render the picker UI always-visible (used by `<ry-color-input>`'s panel).
 *
 * @attribute value      string?              Initial color. Accepts hex (`#ff0000`, `#f00`), rgb(), hsl(), or named colors via `setColor()`. Re-emitted in the configured format.
 * @attribute format     'hex' | 'rgb' | 'hsl' ?   default="hex"   Output format for the `.value` getter and the `[value]` attribute reflection.
 * @attribute opacity    boolean   default=false   Show the alpha slider + display the alpha channel in the value output.
 * @attribute swatches   string?              Comma-separated list of preset colors rendered as a swatch palette below the picker (e.g. `"#ff0000,#00ff00,#0000ff"`).
 * @attribute inline     boolean   default=false   Skip the trigger button + popover; render the picker UI directly. Used when composing inside `<ry-color-input>` or any external panel.
 * @attribute disabled   boolean   default=false   Non-interactive — all controls suppressed.
 *
 * @property  value     string   Read/write — current color in the configured format.
 * @property  rgb       RGB      Read-only — `{r, g, b}` 0-255 (alpha not included).
 * @property  hsl       HSL      Read-only — `{h, s, l}` h 0-360, s/l 0-100.
 * @property  hsv       HSV      Read-only — `{h, s, v}` h 0-360, s/v 0-100. The internal representation.
 *
 * @method    setColor(colorString)  Parse + apply a color string. Returns `true` if parse succeeded, `false` if invalid. Use this when you need to know whether your input was accepted.
 *
 * @event ry:input
 *        detail: { value: string, hsv: HSV, rgb: RGB }
 *        fires:  on every micro-step during interaction — grid drag, hue drag, alpha drag, channel input keystroke, swatch click. Use for live preview.
 *
 * @event ry:beforechange
 *        detail: { value: string, hsv: HSV, rgb: RGB, previous: string }
 *        fires:  before each commit — drag release, handle arrow keys, hex commit, swatch click — with `value`/`hsv`/`rgb` the color ABOUT to be committed and `previous` the current value (for drag release: the color at drag start). Not fired for the `ry:input` stream.
 *        cancelable: preventDefault() keeps `previous` — a vetoed drag release snaps the handles back to where the drag started, a vetoed hex commit resets the input — and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string, hsv: HSV, rgb: RGB }
 *        fires:  on commit — drag release, channel input blur, hex commit, swatch click. Same shape as `ry:input` but throttled.
 *
 * @keyboard  Hex input: standard text-input semantics. Channel inputs: arrow keys step ±1, Shift+Arrow ±10. Grid + sliders: pointer-only by default (vitest skips drag).
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` for the popover (non-inline mode only).
 *
 * @content   None — `<ry-color-picker>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-color-picker[value][format?][opacity?][swatches?][inline?][disabled?]
 *     (non-inline, popover mode)
 *     > button.ry-color-picker__trigger   [data-ry-target="trigger"]   (swatch button)
 *     > div.ry-color-picker__panel        [data-ry-target="panel"]
 *     (inline mode renders the panel content directly)
 *       > div.ry-color-picker__grid       [data-ry-target="grid"]   (saturation × value square)
 *       > div.ry-color-picker__controls   [data-ry-target="controls"]   {sliders + preview + input row}
 *         > div.ry-color-picker__hue        [data-ry-target="hue-slider"]
 *         > div.ry-color-picker__alpha      [data-ry-target="alpha-slider"]  (only when [opacity])
 *         > div [data-ry-target="input-row"]   {hex + format toggle + RGB/HSL inputs}
 *       > div.ry-color-picker__swatches   [data-ry-target="swatches"]   (only when [swatches])
 *
 * @gotcha  Pointer-drag interactions on the grid / hue / alpha sliders need real layout (`getBoundingClientRect` + pointer coordinates). happy-dom can't simulate them — vitest covers programmatic + setColor + channel input paths only.
 *
 * @gotcha  `setColor("invalid")` returns `false` and the picker stays unchanged. The setter `el.value = "invalid"` calls `setColor` internally + then returns; same silent-no-op pattern.
 *
 * @gotcha  RGB / HSL values are derived from the internal HSV representation. Small rounding drift may occur after multiple format conversions (e.g. setting `value="#7f7f7f"` then reading `el.rgb` may give `{r: 127, g: 127, b: 127}` not `{r: 128, ...}`). For exact precision use the hex format.
 *
 * @gotcha  Alpha channel state is ALWAYS tracked (the picker has an internal alpha value), but it's only EXPOSED in the output value when `[opacity]` is set. Without opacity, `el.value` always returns a fully-opaque color even if the internal alpha was somehow set <1.
 *
 * @gotcha  `[inline]` strips the trigger button — there is no popover state, no open/close events. The picker renders directly. Setting `data-ry-state` becomes meaningless.
 *
 * @example basic
 * ```html
 * <ry-color-picker value="#ff0000"></ry-color-picker>
 * ```
 *
 * @example inline
 * ```html
 * <ry-color-picker value="#ff0000" inline></ry-color-picker>
 * ```
 *
 * @example with-alpha
 * ```html
 * <ry-color-picker value="#ff0000" opacity></ry-color-picker>
 * ```
 *
 * @example rgb-format
 * ```html
 * <ry-color-picker value="rgb(255, 0, 0)" format="rgb"></ry-color-picker>
 * ```
 *
 * @example with-swatches
 * ```html
 * <ry-color-picker value="#ff0000" swatches="#ff0000,#00ff00,#0000ff,#ffff00"></ry-color-picker>
 * ```
 *
 * @tutorial setColor-returns-true-for-valid-inputs
 * ```js
 * const el = mount('<ry-color-picker value="#ffffff" inline></ry-color-picker>');
 * await expectEqual(el.setColor('#ff0000'), true, 'valid hex returns true');
 * await expectEqual(el.setColor('rgb(0, 255, 0)'), true, 'valid rgb returns true');
 * await expectEqual(el.setColor('hsl(240, 100%, 50%)'), true, 'valid hsl returns true');
 * ```
 *
 * @tutorial setColor-returns-false-for-invalid-inputs
 * ```js
 * // Wrong assumption: setColor throws on invalid. It returns false instead.
 * // (Note: expectEqual uses Object.is, so compare individual channels — not
 * // whole objects.)
 * const el = mount('<ry-color-picker value="#ff0000" inline></ry-color-picker>');
 * await expectEqual(el.setColor('not-a-color'), false);
 * await expectEqual(el.setColor(''), false);
 * await expectEqual(el.setColor('hsl(abc)'), false);
 * await expectEqual(el.rgb.r, 255, 'previous color survives');
 * await expectEqual(el.rgb.g, 0);
 * await expectEqual(el.rgb.b, 0);
 * ```
 *
 * @tutorial rgb-getter-from-hex
 * ```js
 * const el = mount('<ry-color-picker value="#ff0000" inline></ry-color-picker>');
 * await expectEqual(el.rgb.r, 255);
 * await expectEqual(el.rgb.g, 0);
 * await expectEqual(el.rgb.b, 0);
 * el.setColor('#00ff00');
 * await expectEqual(el.rgb.r, 0);
 * await expectEqual(el.rgb.g, 255);
 * el.setColor('#0000ff');
 * await expectEqual(el.rgb.b, 255);
 * ```
 *
 * @tutorial short-hex-is-expanded
 * ```js
 * const el = mount('<ry-color-picker value="#000" inline></ry-color-picker>');
 * el.setColor('#f00');
 * await expectEqual(el.rgb.r, 255);
 * await expectEqual(el.rgb.g, 0);
 * await expectEqual(el.rgb.b, 0);
 * ```
 *
 * @tutorial inline-mode-skips-trigger
 * ```js
 * // Wrong assumption: inline is just a CSS flag. It actually changes the
 * // rendered DOM — there's no trigger button.
 * const el = mount('<ry-color-picker value="#ff0000" inline></ry-color-picker>');
 * await expectEqual(el.querySelector('[data-ry-target="trigger"]'), null);
 * ```
 *
 * @tutorial format-controls-value-shape
 * ```js
 * const hex = mount('<ry-color-picker value="#ff0000" inline></ry-color-picker>');
 * await expectEqual(hex.value, '#ff0000');
 * const rgb = mount('<ry-color-picker value="#ff0000" format="rgb" inline></ry-color-picker>');
 * await expectEqual(rgb.value, 'rgb(255, 0, 0)');
 * const hsl = mount('<ry-color-picker value="#ff0000" format="hsl" inline></ry-color-picker>');
 * await expectEqual(hsl.value.startsWith('hsl('), true);
 * ```
 *
 * @tutorial opacity-attribute-renders-alpha-slider
 * ```js
 * const el = mount('<ry-color-picker value="#ff0000" inline opacity></ry-color-picker>');
 * await expectEqual(!!el.querySelector('[data-ry-target="alpha-slider"]'), true, 'alpha slider rendered');
 * const elNo = mount('<ry-color-picker value="#ff0000" inline></ry-color-picker>');
 * await expectEqual(elNo.querySelector('[data-ry-target="alpha-slider"]'), null, 'no alpha without [opacity]');
 * ```
 *
 * @tutorial swatches-render-when-attribute-set
 * ```js
 * const el = mount('<ry-color-picker value="#ff0000" inline swatches="#f00,#0f0,#00f"></ry-color-picker>');
 * await expectEqual(!!el.querySelector('[data-ry-target="swatches"]'), true);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange keeps the current color and ry:change
 * // stays silent — e.g. "only brand colors allowed".
 * const el = mount('<ry-color-picker value="#ff0000" inline swatches="#f00;#0f0;#00f"></ry-color-picker>');
 * const swatches = el.querySelectorAll('.ry-color-picker__swatch');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail.value));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.value === '#00ff00' && e.detail.previous === '#ff0000') e.preventDefault();
 * }, { once: true });
 * swatches[1].click();
 * await expectEqual(el.value, '#ff0000', 'vetoed swatch click leaves the color alone');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * swatches[1].click();
 * await expectEqual(el.value, '#00ff00', 'without the veto, the swatch commits');
 * await expectEqual(changed[0], '#00ff00', 'ry:change fired with the new color');
 * ```
 *
 * @see ry-color-input
 * @see ry-gradient-picker
 */

import { RyElement } from '../core/ry-element.js';
import { attachFloating } from '../core/ry-floating.js';

// ─────────────────────────────────────────────────────────────────────────────
// Color conversion utilities (no external dependencies)
// ─────────────────────────────────────────────────────────────────────────────

export interface HSV {
  h: number; // 0-360
  s: number; // 0-100
  v: number; // 0-100
}

export interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

export interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

function hsvToRgb(hsv: HSV): RGB {
  const h = hsv.h / 360;
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  let r = 0, g = 0, b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

function rgbToHsv(rgb: RGB): HSV {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
}

function hsvToHsl(hsv: HSV): HSL {
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  const l = v * (1 - s / 2);
  const sl = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);

  return {
    h: hsv.h,
    s: Math.round(sl * 100),
    l: Math.round(l * 100)
  };
}

function hslToHsv(hsl: HSL): HSV {
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  const v = l + s * Math.min(l, 1 - l);
  const sv = v === 0 ? 0 : 2 * (1 - l / v);

  return {
    h: hsl.h,
    s: Math.round(sv * 100),
    v: Math.round(v * 100)
  };
}

function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    // Try 3-character hex
    const short = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    if (short) {
      return {
        r: parseInt(short[1]! + short[1]!, 16),
        g: parseInt(short[2]! + short[2]!, 16),
        b: parseInt(short[3]! + short[3]!, 16)
      };
    }
    return null;
  }
  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16)
  };
}

function parseColor(input: string): { hsv: HSV; alpha: number } | null {
  const str = input.trim().toLowerCase();

  // Hex format
  if (str.startsWith('#')) {
    // Check for 8-character hex (with alpha)
    const hexAlpha = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(str);
    if (hexAlpha) {
      const rgb = {
        r: parseInt(hexAlpha[1]!, 16),
        g: parseInt(hexAlpha[2]!, 16),
        b: parseInt(hexAlpha[3]!, 16)
      };
      const alpha = parseInt(hexAlpha[4]!, 16) / 255;
      return { hsv: rgbToHsv(rgb), alpha: Math.round(alpha * 100) };
    }
    const rgb = hexToRgb(str);
    if (rgb) return { hsv: rgbToHsv(rgb), alpha: 100 };
    return null;
  }

  // RGB/RGBA format
  const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/.exec(str);
  if (rgbMatch) {
    const rgb = {
      r: parseInt(rgbMatch[1]!, 10),
      g: parseInt(rgbMatch[2]!, 10),
      b: parseInt(rgbMatch[3]!, 10)
    };
    const alpha = rgbMatch[4] ? parseFloat(rgbMatch[4]) * 100 : 100;
    return { hsv: rgbToHsv(rgb), alpha };
  }

  // HSL/HSLA format
  const hslMatch = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+))?\s*\)$/.exec(str);
  if (hslMatch) {
    const hsl = {
      h: parseFloat(hslMatch[1]!),
      s: parseFloat(hslMatch[2]!),
      l: parseFloat(hslMatch[3]!)
    };
    const alpha = hslMatch[4] ? parseFloat(hslMatch[4]) * 100 : 100;
    return { hsv: hslToHsv(hsl), alpha };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export type ColorFormat = 'hex' | 'rgb' | 'hsl';

/** Full picker color state — what a commit candidate or a drag-start snapshot holds. */
interface ColorState {
  h: number;
  s: number;
  v: number;
  a: number;
}

export class RyColorPicker extends RyElement {
  #hue = 0;
  #saturation = 100;
  #brightness = 100;
  #alpha = 100;

  #draggingGrid = false;
  #draggingHue = false;
  #draggingAlpha = false;
  /** Color at drag start — `previous` for the drag-release commit, restored on veto. */
  #dragStart: ColorState | null = null;

  #trigger: HTMLElement | null = null;
  #panel: HTMLElement | null = null;
  #floatingCleanup: (() => void) | null = null;
  #grid: HTMLElement | null = null;
  #gridHandle: HTMLElement | null = null;
  #hueSlider: HTMLElement | null = null;
  #hueHandle: HTMLElement | null = null;
  #alphaSlider: HTMLElement | null = null;
  #alphaHandle: HTMLElement | null = null;
  #preview: HTMLElement | null = null;
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['value', 'format', 'opacity', 'disabled', 'swatches', 'inline'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#parseInitialValue();
    this.#updateAll();
  }

  #buildMarkup(): void {
    const isInline = this.hasAttribute('inline');
    const showAlpha = this.hasAttribute('opacity');
    const swatches = this.getAttribute('swatches');

    // Build the picker panel
    let panelHtml = `
      <div data-ry-target="grid" class="ry-color-picker__grid">
        <div data-ry-target="grid-handle" class="ry-color-picker__grid-handle" tabindex="0"></div>
      </div>
      <div data-ry-target="controls" class="ry-color-picker__controls">
        <div data-ry-target="sliders" class="ry-color-picker__sliders">
          <div data-ry-target="hue-slider" class="ry-color-picker__slider ry-color-picker__hue">
            <div data-ry-target="hue-handle" class="ry-color-picker__slider-handle" tabindex="0"></div>
          </div>
          ${showAlpha ? `
          <div data-ry-target="alpha-slider" class="ry-color-picker__slider ry-color-picker__alpha">
            <div data-ry-target="alpha-handle" class="ry-color-picker__slider-handle" tabindex="0"></div>
          </div>
          ` : ''}
        </div>
        <div data-ry-target="preview" class="ry-color-picker__preview">
          <span data-ry-target="preview-color" class="ry-color-picker__preview-color"></span>
        </div>
      </div>
      <div data-ry-target="input-row" class="ry-color-picker__input-row">
        <input data-ry-target="input" type="text" class="ry-color-picker__input" spellcheck="false" />
        <button data-ry-target="format-toggle" type="button" class="ry-color-picker__format-toggle">${this.format.toUpperCase()}</button>
      </div>
    `;

    // Add swatches if provided
    if (swatches) {
      const colors = swatches.split(';').map(c => c.trim()).filter(Boolean);
      if (colors.length > 0) {
        panelHtml += `<div data-ry-target="swatches" class="ry-color-picker__swatches">`;
        for (const color of colors) {
          panelHtml += `<button type="button" class="ry-color-picker__swatch" data-color="${color}" style="background-color: ${color}"></button>`;
        }
        panelHtml += `</div>`;
      }
    }

    if (isInline) {
      // Inline mode: just show the panel
      this.innerHTML = `<div data-ry-target="panel" class="ry-color-picker__panel ry-color-picker__panel--inline">${panelHtml}</div>`;
    } else {
      // Dropdown mode: trigger + panel
      this.innerHTML = `
        <button data-ry-target="trigger" type="button" class="ry-color-picker__trigger">
          <span data-ry-target="trigger-color" class="ry-color-picker__trigger-color"></span>
        </button>
        <div data-ry-target="panel" class="ry-color-picker__panel">${panelHtml}</div>
      `;
    }

    // Cache references
    this.#trigger = this.$('[data-ry-target="trigger"]');
    this.#panel = this.$('[data-ry-target="panel"]');
    this.#grid = this.$('[data-ry-target="grid"]');
    this.#gridHandle = this.$('[data-ry-target="grid-handle"]');
    this.#hueSlider = this.$('[data-ry-target="hue-slider"]');
    this.#hueHandle = this.$('[data-ry-target="hue-handle"]');
    this.#alphaSlider = this.$('[data-ry-target="alpha-slider"]');
    this.#alphaHandle = this.$('[data-ry-target="alpha-handle"]');
    this.#preview = this.$('[data-ry-target="preview"]');
    this.#input = this.$('[data-ry-target="input"]');
  }

  #bindEvents(): void {
    // Trigger click (dropdown mode)
    if (this.#trigger) {
      this.on(this.#trigger, 'click', this.#handleTriggerClick);
    }

    // Click outside to close
    this.on(document, 'click', this.#handleDocumentClick);

    // Escape to close
    this.on(document, 'keydown', this.#handleDocumentKeydown);

    // Grid dragging
    if (this.#grid) {
      this.on(this.#grid, 'pointerdown', this.#handleGridDown);
    }
    if (this.#gridHandle) {
      this.on(this.#gridHandle, 'keydown', this.#handleGridKeydown);
    }

    // Hue slider
    if (this.#hueSlider) {
      this.on(this.#hueSlider, 'pointerdown', this.#handleHueDown);
    }
    if (this.#hueHandle) {
      this.on(this.#hueHandle, 'keydown', this.#handleHueKeydown);
    }

    // Alpha slider
    if (this.#alphaSlider) {
      this.on(this.#alphaSlider, 'pointerdown', this.#handleAlphaDown);
    }
    if (this.#alphaHandle) {
      this.on(this.#alphaHandle, 'keydown', this.#handleAlphaKeydown);
    }

    // Document events for drag
    this.on(document, 'pointermove', this.#handlePointerMove);
    this.on(document, 'pointerup', this.#handlePointerUp);

    // Input
    if (this.#input) {
      this.on(this.#input, 'change', this.#handleInputChange);
      this.on(this.#input, 'keydown', this.#handleInputKeydown);
    }

    // Format toggle
    const formatToggle = this.$<HTMLButtonElement>('[data-ry-target="format-toggle"]');
    if (formatToggle) {
      this.on(formatToggle, 'click', this.#handleFormatToggle);
    }

    // Swatches
    const swatchContainer = this.$('[data-ry-target="swatches"]');
    if (swatchContainer) {
      this.on(swatchContainer, 'click', this.#handleSwatchClick);
    }
  }

  #parseInitialValue(): void {
    const value = this.getAttribute('value');
    if (value) {
      const parsed = parseColor(value);
      if (parsed) {
        this.#hue = parsed.hsv.h;
        this.#saturation = parsed.hsv.s;
        this.#brightness = parsed.hsv.v;
        this.#alpha = parsed.alpha;
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Dropdown handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleTriggerClick = (): void => {
    if (this.disabled) return;
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  };

  #handleDocumentClick = (e: Event): void => {
    if (this.state !== 'open') return;
    if (!this.contains(e.target as Node)) {
      this.close();
    }
  };

  #handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.state === 'open') {
      this.close();
      this.#trigger?.focus();
    }
  };

  open(): void {
    if (this.hasAttribute('inline')) return;
    this.state = 'open';
    // Mirror state onto the panel so CSS keyed on the panel's own
    // data-ry-state works (survives any future portal-to-body migration).
    // The host's data-ry-state is reserved for events / ARIA — descendant
    // selectors against it break the moment the panel portals. See
    // docs/PROJECT_STATE.md Hard Rule 3 + ry-popover for the canonical pattern.
    this.#panel?.setAttribute('data-ry-state', 'open');
    this.#attachFloating();
  }

  close(): void {
    if (this.hasAttribute('inline')) return;
    this.state = 'closed';
    this.#panel?.setAttribute('data-ry-state', 'closed');
    this.#detachFloating();
  }

  #attachFloating(): void {
    if (!this.#panel || !this.#trigger) return;
    this.#floatingCleanup = attachFloating(this.#trigger, this.#panel, {
      placement: 'bottom-start',
      offset: 4,
      flip: true,
      shift: true,
      reflectPlacementAttribute: true,
    });
  }

  #detachFloating(): void {
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Grid handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleGridDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingGrid = true;
    this.#dragStart = this.#snapshot();
    this.#gridHandle?.focus();
    this.#updateFromGridPointer(e);
  };

  #updateFromGridPointer(e: PointerEvent): void {
    if (!this.#grid) return;
    const rect = this.#grid.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    this.#saturation = Math.round(x * 100);
    this.#brightness = Math.round((1 - y) * 100);
    this.#updateAll();
    this.#emitInput();
  }

  #handleGridKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    const next = this.#snapshot();
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
        next.s = Math.min(100, next.s + step);
        changed = true;
        break;
      case 'ArrowLeft':
        next.s = Math.max(0, next.s - step);
        changed = true;
        break;
      case 'ArrowUp':
        next.v = Math.min(100, next.v + step);
        changed = true;
        break;
      case 'ArrowDown':
        next.v = Math.max(0, next.v - step);
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#commit(next, true);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Hue handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleHueDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingHue = true;
    this.#dragStart = this.#snapshot();
    this.#hueHandle?.focus();
    this.#updateFromHuePointer(e);
  };

  #updateFromHuePointer(e: PointerEvent): void {
    if (!this.#hueSlider) return;
    const rect = this.#hueSlider.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this.#hue = Math.round(x * 360);
    this.#updateAll();
    this.#emitInput();
  }

  #handleHueKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    const next = this.#snapshot();
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next.h = (next.h + step) % 360;
        changed = true;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next.h = (next.h - step + 360) % 360;
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#commit(next, true);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Alpha handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleAlphaDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingAlpha = true;
    this.#dragStart = this.#snapshot();
    this.#alphaHandle?.focus();
    this.#updateFromAlphaPointer(e);
  };

  #updateFromAlphaPointer(e: PointerEvent): void {
    if (!this.#alphaSlider) return;
    const rect = this.#alphaSlider.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this.#alpha = Math.round(x * 100);
    this.#updateAll();
    this.#emitInput();
  }

  #handleAlphaKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    const next = this.#snapshot();
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next.a = Math.min(100, next.a + step);
        changed = true;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next.a = Math.max(0, next.a - step);
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#commit(next, true);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Pointer move/up
  // ───────────────────────────────────────────────────────────────────────────

  #handlePointerMove = (e: PointerEvent): void => {
    if (this.#draggingGrid) {
      this.#updateFromGridPointer(e);
    } else if (this.#draggingHue) {
      this.#updateFromHuePointer(e);
    } else if (this.#draggingAlpha) {
      this.#updateFromAlphaPointer(e);
    }
  };

  #handlePointerUp = (): void => {
    if (this.#draggingGrid || this.#draggingHue || this.#draggingAlpha) {
      this.#draggingGrid = false;
      this.#draggingHue = false;
      this.#draggingAlpha = false;
      // The color already moved live (ry:input per frame); the commit is
      // judged against the drag-start snapshot, and a veto snaps back to it.
      const start = this.#dragStart ?? this.#snapshot();
      this.#dragStart = null;
      if (!this.emit('beforechange', {
        value: this.value, hsv: this.hsv, rgb: this.rgb, previous: this.#formatValue(start),
      })) {
        this.#restore(start);
        this.#updateAll();
        return;
      }
      this.#emitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Input handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleInputChange = (): void => {
    if (!this.#input) return;
    const parsed = parseColor(this.#input.value);
    if (parsed) {
      const next: ColorState = {
        h: parsed.hsv.h, s: parsed.hsv.s, v: parsed.hsv.v,
        a: this.hasAttribute('opacity') ? parsed.alpha : this.#alpha,
      };
      // A vetoed commit resets the input to the current value, like invalid input.
      if (!this.#commit(next, false)) this.#updateInput();
    } else {
      // Reset to current value on invalid input
      this.#updateInput();
    }
  };

  #handleInputKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#handleInputChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Format toggle
  // ───────────────────────────────────────────────────────────────────────────

  #handleFormatToggle = (): void => {
    const formats: ColorFormat[] = ['hex', 'rgb', 'hsl'];
    const current = this.format;
    const idx = formats.indexOf(current);
    const next = formats[(idx + 1) % formats.length]!;
    this.format = next;

    const btn = this.$<HTMLButtonElement>('[data-ry-target="format-toggle"]');
    if (btn) btn.textContent = next.toUpperCase();

    this.#updateInput();
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Swatches
  // ───────────────────────────────────────────────────────────────────────────

  #handleSwatchClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('ry-color-picker__swatch')) {
      const color = target.dataset.color;
      if (color) {
        const parsed = parseColor(color);
        if (parsed) {
          this.#commit({
            h: parsed.hsv.h, s: parsed.hsv.s, v: parsed.hsv.v,
            a: this.hasAttribute('opacity') ? parsed.alpha : this.#alpha,
          }, true);
        }
      }
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Update methods
  // ───────────────────────────────────────────────────────────────────────────

  #updateAll(): void {
    this.#updateTrigger();
    this.#updateGrid();
    this.#updateHueSlider();
    this.#updateAlphaSlider();
    this.#updatePreview();
    this.#updateInput();
    this.#syncValueAttribute();
  }

  #updateTrigger(): void {
    const triggerColor = this.$('[data-ry-target="trigger-color"]');
    if (triggerColor) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      const alpha = this.hasAttribute('opacity') ? this.#alpha / 100 : 1;
      (triggerColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #updateGrid(): void {
    if (this.#grid) {
      // Set grid background to current hue at full saturation
      const rgb = hsvToRgb({ h: this.#hue, s: 100, v: 100 });
      this.#grid.style.setProperty('--grid-hue-color', `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    }
    if (this.#gridHandle) {
      this.#gridHandle.style.left = `${this.#saturation}%`;
      this.#gridHandle.style.top = `${100 - this.#brightness}%`;
    }
  }

  #updateHueSlider(): void {
    if (this.#hueHandle) {
      this.#hueHandle.style.left = `${(this.#hue / 360) * 100}%`;
    }
  }

  #updateAlphaSlider(): void {
    if (this.#alphaSlider) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      this.#alphaSlider.style.setProperty('--alpha-color', `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    }
    if (this.#alphaHandle) {
      this.#alphaHandle.style.left = `${this.#alpha}%`;
    }
  }

  #updatePreview(): void {
    const previewColor = this.$('[data-ry-target="preview-color"]');
    if (previewColor) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      const alpha = this.hasAttribute('opacity') ? this.#alpha / 100 : 1;
      (previewColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #updateInput(): void {
    if (this.#input) {
      this.#input.value = this.#formatValue();
    }
  }

  #syncValueAttribute(): void {
    // Update the value attribute without triggering attributeChangedCallback loop
    const formatted = this.#formatValue();
    if (this.getAttribute('value') !== formatted) {
      this.setAttribute('value', formatted);
    }
  }

  /** Format a color state (defaults to the current one) in the configured format. */
  #formatValue(c: ColorState = this.#snapshot()): string {
    const format = this.format;
    const rgb = hsvToRgb({ h: c.h, s: c.s, v: c.v });
    const showAlpha = this.hasAttribute('opacity');

    switch (format) {
      case 'hex': {
        const hex = rgbToHex(rgb);
        if (showAlpha && c.a < 100) {
          const alphaHex = Math.round((c.a / 100) * 255).toString(16).padStart(2, '0');
          return hex + alphaHex;
        }
        return hex;
      }
      case 'rgb': {
        if (showAlpha) {
          return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(c.a / 100).toFixed(2)})`;
        }
        return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      }
      case 'hsl': {
        const hsl = hsvToHsl({ h: c.h, s: c.s, v: c.v });
        if (showAlpha) {
          return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${(c.a / 100).toFixed(2)})`;
        }
        return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
      }
      default:
        return rgbToHex(rgb);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Events
  // ───────────────────────────────────────────────────────────────────────────

  #emitInput(): void {
    this.emit('input', { value: this.value, hsv: this.hsv, rgb: this.rgb });
  }

  #emitChange(): void {
    this.emit('change', { value: this.value, hsv: this.hsv, rgb: this.rgb });
  }

  #snapshot(): ColorState {
    return { h: this.#hue, s: this.#saturation, v: this.#brightness, a: this.#alpha };
  }

  #restore(c: ColorState): void {
    this.#hue = c.h;
    this.#saturation = c.s;
    this.#brightness = c.v;
    this.#alpha = c.a;
  }

  /**
   * The commit choke point for discrete changes (keys, hex, swatch):
   * beforechange describes the candidate, and only an un-vetoed one is applied.
   * Returns whether the commit landed.
   */
  #commit(next: ColorState, withInput: boolean): boolean {
    const hsv: HSV = { h: next.h, s: next.s, v: next.v };
    if (!this.emit('beforechange', {
      value: this.#formatValue(next), hsv, rgb: hsvToRgb(hsv), previous: this.value,
    })) return false;
    this.#restore(next);
    this.#updateAll();
    if (withInput) this.#emitInput();
    this.#emitChange();
    return true;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Attribute changed
  // ───────────────────────────────────────────────────────────────────────────

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'value' && this.#grid) {
      // External value change - parse and update
      if (newValue) {
        const parsed = parseColor(newValue);
        if (parsed) {
          this.#hue = parsed.hsv.h;
          this.#saturation = parsed.hsv.s;
          this.#brightness = parsed.hsv.v;
          if (this.hasAttribute('opacity')) {
            this.#alpha = parsed.alpha;
          }
          this.#updateTrigger();
          this.#updateGrid();
          this.#updateHueSlider();
          this.#updateAlphaSlider();
          this.#updatePreview();
          // Don't update input here to avoid cursor issues during typing
        }
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Public API
  // ───────────────────────────────────────────────────────────────────────────

  get value(): string {
    return this.#formatValue();
  }

  set value(val: string) {
    const parsed = parseColor(val);
    if (parsed) {
      this.#hue = parsed.hsv.h;
      this.#saturation = parsed.hsv.s;
      this.#brightness = parsed.hsv.v;
      if (this.hasAttribute('opacity')) {
        this.#alpha = parsed.alpha;
      }
      this.#updateAll();
    }
  }

  get format(): ColorFormat {
    return (this.getAttribute('format') as ColorFormat) || 'hex';
  }

  set format(val: ColorFormat) {
    this.setAttribute('format', val);
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get hsv(): HSV {
    return { h: this.#hue, s: this.#saturation, v: this.#brightness };
  }

  get rgb(): RGB {
    return hsvToRgb(this.hsv);
  }

  get hsl(): HSL {
    return hsvToHsl(this.hsv);
  }

  get alpha(): number {
    return this.#alpha;
  }

  set alpha(val: number) {
    this.#alpha = Math.max(0, Math.min(100, val));
    this.#updateAll();
  }

  /** Set color from any valid color string */
  setColor(colorString: string): boolean {
    const parsed = parseColor(colorString);
    if (parsed) {
      this.#hue = parsed.hsv.h;
      this.#saturation = parsed.hsv.s;
      this.#brightness = parsed.hsv.v;
      if (this.hasAttribute('opacity')) {
        this.#alpha = parsed.alpha;
      }
      this.#updateAll();
      return true;
    }
    return false;
  }

  /** Get color in specific format */
  getFormattedValue(format: ColorFormat): string {
    const originalFormat = this.format;
    this.format = format;
    const value = this.#formatValue();
    this.format = originalFormat;
    return value;
  }
}

customElements.define('ry-color-picker', RyColorPicker);
